import {
  PostQrCodeAlignHorizontal,
  PostQrCodeAlignVertical,
  type PostQrCodeStyle,
} from "@package/ui-gql/index-data-access";
import type { CSSProperties } from "react";
import {
  POST_QR_CODE_BORDER_WIDTH,
  POST_QR_CODE_BOX_PADDING,
  POST_QR_CODE_BOX_SHAPE_TO_RADIUS,
  POST_QR_CODE_DEFAULT_BACKDROP_COLOR,
  POST_QR_CODE_DEFAULT_BORDER_COLOR,
  POST_QR_CODE_DEFAULT_BOX_COLOR,
  POST_QR_CODE_DEFAULT_QR_BACKGROUND_COLOR,
  POST_QR_CODE_DEFAULT_QR_COLOR,
  POST_QR_CODE_DEFAULT_TEXT_COLOR,
  POST_QR_CODE_DEFAULT_TEXT_SIZE,
} from "../../constants/constants";

export type PostQrCodeResolvedStyle = {
  backdropColor: string;
  boxColor: string;
  boxRadius: string;
  border?: string;
  textColor: string;
  textSize: number;
  textHidden: boolean;
  qrColor: string;
  qrBackgroundColor: string;
  qrSize?: number;
  qrAlignHorizontal: PostQrCodeAlignHorizontal;
  qrAlignVertical: PostQrCodeAlignVertical;
  textAlignHorizontal: PostQrCodeAlignHorizontal;
  textAlignVertical: PostQrCodeAlignVertical;
};

export function resolvePostQrCodeStyle(
  style?: PostQrCodeStyle | null,
): PostQrCodeResolvedStyle {
  return {
    backdropColor: style?.backdropColor ?? POST_QR_CODE_DEFAULT_BACKDROP_COLOR,
    boxColor: style?.boxColor ?? POST_QR_CODE_DEFAULT_BOX_COLOR,
    boxRadius: POST_QR_CODE_BOX_SHAPE_TO_RADIUS[style?.boxShape ?? "rounded"],
    border: style?.borderEnabled
      ? `${POST_QR_CODE_BORDER_WIDTH}px solid ${
          style?.borderColor ?? POST_QR_CODE_DEFAULT_BORDER_COLOR
        }`
      : undefined,
    textColor: style?.textColor ?? POST_QR_CODE_DEFAULT_TEXT_COLOR,
    textSize: style?.textSize ?? POST_QR_CODE_DEFAULT_TEXT_SIZE,
    textHidden: style?.textHidden ?? false,
    qrColor: style?.qrColor ?? POST_QR_CODE_DEFAULT_QR_COLOR,
    qrBackgroundColor:
      style?.qrBackgroundColor ?? POST_QR_CODE_DEFAULT_QR_BACKGROUND_COLOR,
    qrSize: style?.qrSize ?? undefined,
    qrAlignHorizontal:
      style?.qrAlignHorizontal ?? PostQrCodeAlignHorizontal.Center,
    qrAlignVertical: style?.qrAlignVertical ?? PostQrCodeAlignVertical.Center,
    // Defaults match the caption's original fixed position — bottom of the
    // box, centered. On their own these don't guarantee unchanged rendering
    // for existing posts; that also depends on the backdropColor default
    // above reproducing the box's original halo.
    textAlignHorizontal:
      style?.textAlignHorizontal ?? PostQrCodeAlignHorizontal.Center,
    textAlignVertical:
      style?.textAlignVertical ?? PostQrCodeAlignVertical.Bottom,
  };
}

// Anchors the QR within the box, inset by the box's own padding on
// whichever edges it's pinned to, so it never sits flush against the
// border. Only meaningful when the box has real extra space (the `fill`
// variant of PostQrCodeBox) — an intrinsically-sized box hugs its content
// regardless of anchor.
export function getPostQrCodeAnchorStyle(
  horizontal: PostQrCodeAlignHorizontal,
  vertical: PostQrCodeAlignVertical,
): CSSProperties {
  const style: CSSProperties = { position: "absolute" };
  const translateX = horizontal === PostQrCodeAlignHorizontal.Center;
  const translateY = vertical === PostQrCodeAlignVertical.Center;

  if (horizontal === PostQrCodeAlignHorizontal.Left) {
    style.left = POST_QR_CODE_BOX_PADDING;
  } else if (horizontal === PostQrCodeAlignHorizontal.Right) {
    style.right = POST_QR_CODE_BOX_PADDING;
  } else {
    style.left = "50%";
  }

  if (vertical === PostQrCodeAlignVertical.Top) {
    style.top = POST_QR_CODE_BOX_PADDING;
  } else if (vertical === PostQrCodeAlignVertical.Bottom) {
    style.bottom = POST_QR_CODE_BOX_PADDING;
  } else {
    style.top = "50%";
  }

  if (translateX || translateY) {
    style.transform = `translate(${translateX ? "-50%" : "0"}, ${
      translateY ? "-50%" : "0"
    })`;
  }

  return style;
}
